import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import { verifyGoogleState } from '../utils/googleAuthUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Mid-flight retry buffer
 * ───────────────────────
 * Emergent's session-id has a ~30-second server-side TTL. If the user
 * backgrounds the tab, hits a transient network blip, or their phone
 * sleeps between the OAuth redirect and our exchange call, the exchange
 * fails *silently* and we lose the user.
 *
 * Strategy (4 layers):
 *   1. **Persist immediately.** Stash the session_id in sessionStorage
 *      BEFORE the network call. If a backgrounded tab kills the JS
 *      runtime, the next mount can resume.
 *   2. **Exponential retry on transient failures.** 5xx, network, and
 *      502 codes get up to 3 retries with 1.5x backoff (1s → 1.5s → 2.25s).
 *   3. **Visibility-resume.** If the tab is hidden/foregrounded during
 *      the exchange, fire one more attempt the moment it becomes
 *      visible again.
 *   4. **Don't retry 401.** A 401 means Emergent expired the session_id
 *      — no amount of retrying brings it back; we route to /login and
 *      tell the user honestly.
 */
const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 1000;
const STASH_KEY = 'shopperden_pending_google_exchange';

const dashboardForRole = (role) => {
  // `investor` is a buyer subtype — same UI, different framing — so it
  // lands on the buyer dashboard. Keep this map in sync with
  // UnifiedAuthPanel.dashboardForRole.
  const map = {
    admin: '/admin', seller: '/seller', buyer: '/buyer', agent: '/agent',
    professional: '/professional', tenant: '/tenant', investor: '/buyer',
  };
  return map[role] || '/buyer';
};

const isRetryable = (err) => {
  const status = err?.response?.status;
  if (status === 401 || status === 400) return false;       // session dead — no point
  if (status === 502 || status === 503 || status === 504) return true;
  if (status >= 500) return true;
  if (!status) return true;                                 // pure network failure
  return false;
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const inFlight = useRef(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // ── DIRECT-FLOW DETECTION (self-owned Google OAuth) ────────────────
    // Google's authorization-code flow returns ?code=XXX&state=YYY in
    // the query string. If we see a `code` param, exchange it directly
    // with our backend's /api/auth/google/exchange — bypassing the
    // legacy Emergent bridge below.
    const queryParams = new URLSearchParams(location.search || window.location.search);
    const oauthCode = queryParams.get('code');
    const oauthState = queryParams.get('state');
    const oauthError = queryParams.get('error');

    if (oauthError) {
      toast.error(`Google sign-in cancelled: ${oauthError}`);
      navigate('/login');
      return;
    }

    if (oauthCode) {
      if (!verifyGoogleState(oauthState)) {
        toast.error('Sign-in state mismatch — please try again');
        trackEvent('auth_google_state_mismatch', '/auth/callback', {});
        navigate('/login');
        return;
      }

      // Replay consent metadata captured pre-redirect
      let directConsent = {};
      try {
        const raw = sessionStorage.getItem('shopperden_consent');
        if (raw) directConsent = JSON.parse(raw);
      } catch (_e) { /* ignore */ }

      const redirectUri = `${window.location.origin}/auth/callback`;

      (async () => {
        try {
          const r = await axios.post(
            `${API_URL}/api/auth/google/exchange`,
            {
              code: oauthCode,
              redirect_uri: redirectUri,
              consent_country: directConsent.country,
              consent_regime: directConsent.regime,
              consent_at: directConsent.agreed_at,
              role: directConsent.role,
              country: directConsent.country,
            },
            { withCredentials: true, timeout: 15000 },
          );
          if (r.data?.token) {
            try {
              localStorage.setItem('token', r.data.token);
              sessionStorage.setItem('shopperden_access_token', btoa(r.data.token));
            } catch (_e) { /* ignore */ }
          }
          try {
            localStorage.setItem('shopperden_last_auth', JSON.stringify({
              method: 'google',
              email: r.data?.email || '',
              name: r.data?.name || '',
              picture: r.data?.picture || '',
              country: r.data?.country || '',
              role: r.data?.role || '',
              last_at: new Date().toISOString(),
            }));
          } catch (_e) { /* ignore */ }
          sessionStorage.removeItem('shopperden_consent');
          trackEvent('auth_google_success', '/auth/callback', {
            country: r.data?.country, role: r.data?.role,
            flow: 'direct',
          });
          toast.success(`Welcome${r.data?.name ? ', ' + r.data.name.split(' ')[0] : ''}!`);
          window.history.replaceState(null, '', window.location.pathname);
          // Ensure AuthContext picks up the new session before we navigate
          try { await checkAuth(); } catch (_e) { /* ignore */ }
          navigate(dashboardForRole(r.data?.role));
        } catch (err) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail;
          trackEvent('auth_google_failure', '/auth/callback', {
            status, detail: typeof detail === 'string' ? detail : 'unknown',
            flow: 'direct',
          });
          toast.error(typeof detail === 'string'
            ? `Google sign-in failed: ${detail}`
            : 'Google sign-in failed — please try again');
          navigate('/login');
        }
      })();
      return;
    }

    // ── LEGACY BRIDGE FLOW (Emergent-managed Google Auth) ──────────────

    // Pull session_id from fragment OR from a previous stash that the
    // last (interrupted) mount left behind.
    const hash = location.hash || window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    let sessionId = match ? decodeURIComponent(match[1]) : null;

    let stashedAt = null;
    if (!sessionId) {
      try {
        const stash = sessionStorage.getItem(STASH_KEY);
        if (stash) {
          const parsed = JSON.parse(stash);
          sessionId = parsed?.session_id || null;
          stashedAt = parsed?.stashed_at || null;
        }
      } catch (_e) { /* ignore */ }
    }

    if (!sessionId) {
      toast.error('Authentication failed: no session id received');
      navigate('/login');
      return;
    }

    // Stash immediately so a foregrounded re-mount can resume.
    // Emergent's TTL is ~30s; if we're already past that, skip the
    // retry budget and go straight to /login to avoid frustrating the
    // user with three doomed retries.
    if (!stashedAt) {
      stashedAt = new Date().toISOString();
      try {
        sessionStorage.setItem(STASH_KEY, JSON.stringify({
          session_id: sessionId,
          stashed_at: stashedAt,
        }));
      } catch (_e) { /* sessionStorage may be blocked */ }
    }
    const ageSeconds = (Date.now() - new Date(stashedAt).getTime()) / 1000;
    if (ageSeconds > 35) {
      // Past Emergent's TTL window — clear stash, fail fast.
      try { sessionStorage.removeItem(STASH_KEY); } catch (_e) { /* ignore */ }
      toast.error('Google sign-in took too long — please try again.');
      trackEvent('registration_error', '/auth/callback', {
        reason: 'google_session_stale_on_resume',
        age_seconds: Math.round(ageSeconds),
      });
      navigate('/login');
      return;
    }

    // Replay consent metadata captured pre-redirect
    let consent = {};
    try {
      const raw = sessionStorage.getItem('shopperden_consent');
      if (raw) consent = JSON.parse(raw);
    } catch (_e) { /* ignore */ }

    const attemptExchange = async (n = 1) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setAttempt(n);
      try {
        const r = await axios.post(
          `${API_URL}/api/auth/google/session`,
          { session_id: sessionId, ...consent },
          { withCredentials: true, timeout: 12000 },
        );
        if (r.data?.token) {
          sessionStorage.setItem('shopperden_access_token', btoa(r.data.token));
        }
        try {
          localStorage.setItem('shopperden_last_auth', JSON.stringify({
            method: 'google',
            email: r.data?.email || '',
            name: r.data?.name || '',
            picture: r.data?.picture || '',
            country: r.data?.country || '',
            role: r.data?.role || '',
            last_at: new Date().toISOString(),
          }));
        } catch (_e) { /* localStorage may be blocked */ }
        sessionStorage.removeItem('shopperden_consent');
        sessionStorage.removeItem(STASH_KEY);
        trackEvent('auth_google_success', '/auth/callback', {
          country: r.data?.country, role: r.data?.role,
          attempts: n,
        });
        toast.success(`Welcome${r.data?.name ? ', ' + r.data.name.split(' ')[0] : ''}!`);
        window.history.replaceState(null, '', window.location.pathname);
        navigate(dashboardForRole(r.data?.role));
      } catch (err) {
        inFlight.current = false;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;

        // Should we retry?
        if (isRetryable(err) && n < MAX_ATTEMPTS) {
          const delay = Math.round(BASE_DELAY_MS * Math.pow(1.5, n - 1));
          trackEvent('auth_google_retry', '/auth/callback', {
            attempt: n, status: status || 0, delay_ms: delay,
          });
          setTimeout(() => {
            hasProcessed.current = false;  // allow re-entry to attemptExchange
            attemptExchange(n + 1);
          }, delay);
          return;
        }

        // Out of retries OR non-retryable — give up cleanly.
        sessionStorage.removeItem(STASH_KEY);
        const msg = detail
          || (status === 401 ? 'Google session expired — please sign in again.'
            : status === 502 || status === 503 ? 'Auth provider temporarily unreachable — please retry in 30 seconds.'
            : status === 400 ? 'Invalid sign-in request — please retry from the login page.'
            : status === 404 ? 'Sign-in route missing — backend may not be redeployed yet.'
            : status >= 500 ? `Server error (${status}) — please retry.`
            : err?.message?.includes('Network') ? 'Network blocked — check cookies and try again.'
            : 'Could not complete Google sign-in.');
        toast.error(msg);
        trackEvent('registration_error', '/auth/callback', {
          reason: 'google_session_exchange_failed',
          status: status || 0,
          attempts: n,
          detail: String(detail || err?.message || '').slice(0, 200),
        });
        navigate('/login');
      }
    };

    attemptExchange(1);

    // Visibility-resume — if the user backgrounded the tab during the
    // OAuth redirect (common on mobile when they switched to their
    // Google account picker), the moment they return we re-attempt
    // the exchange (provided we still have a pending stash).
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !inFlight.current) {
        const stash = (() => {
          try { return JSON.parse(sessionStorage.getItem(STASH_KEY) || 'null'); } catch (_e) { return null; }
        })();
        if (stash?.session_id) {
          // Reset processed flag to allow another attempt, but cap at MAX_ATTEMPTS overall.
          hasProcessed.current = false;
          attemptExchange(Math.min(attempt + 1, MAX_ATTEMPTS));
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [location.hash, navigate, checkAuth, attempt]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A]" data-testid="auth-callback">
      <div className="text-center text-white/80">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-[#D4AF37]" />
        <p className="text-sm tracking-widest uppercase">Signing you in…</p>
        {attempt > 1 && (
          <p className="text-xs text-white/50 mt-2" data-testid="auth-callback-retry">
            Retry {attempt} of {MAX_ATTEMPTS}…
          </p>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
